import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trash2, 
  RotateCcw, 
  AlertTriangle,
  Search,
  RefreshCw
} from 'lucide-react';

const DeletedModelCard = ({ model, onRestore, onPermanentDelete, index }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-2xl p-6 border-2 border-red-200 shadow-sm relative opacity-75"
    >
      {/* Deleted Badge */}
      <div className="absolute top-4 right-4 px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-lg shadow-lg">
        DELETED
      </div>

      {/* Model Info */}
      <div className="mb-4 pr-24">
        <h3 className="text-lg font-black text-slate-900 truncate mb-1">{model.name}</h3>
        <p className="text-xs font-mono text-slate-500 truncate">{model.model_id}</p>
        {model.tier && (
          <span className="inline-block mt-2 px-2 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg">
            {model.tier}
          </span>
        )}
      </div>

      {/* Provider */}
      <div className="flex items-center gap-2 mb-4">
        <div className="px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
          <span className="text-xs font-bold text-slate-700 capitalize">{model.provider}</span>
        </div>
      </div>

      {/* Deleted Info */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
        <p className="text-xs text-red-700 font-semibold">
          Deleted on: {new Date(model.deleted_at).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t border-slate-200">
        <button
          onClick={() => onRestore(model)}
          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30"
        >
          <RotateCcw className="w-4 h-4" />
          Restore
        </button>
        <button
          onClick={() => onPermanentDelete(model)}
          className="flex-1 px-4 py-2.5 bg-red-100 hover:bg-red-200 border-2 border-red-300 text-red-700 font-bold rounded-xl transition-all flex items-center justify-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete Forever
        </button>
      </div>
    </motion.div>
  );
};

export function RecycleBinView({ deletedModels, onRestore, onPermanentDelete, onReload, loading }) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredModels = deletedModels.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.model_id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">Recycle Bin</h2>
          <p className="text-sm text-slate-500 font-medium mt-1">
            Restore or permanently delete models
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onReload}
          disabled={loading}
          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border-2 border-slate-300 rounded-xl text-slate-700 font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </motion.button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-900 mb-1">
              Models in Recycle Bin
            </p>
            <p className="text-xs text-amber-800 font-medium">
              Deleted models are kept here temporarily. You can restore them or permanently delete them. 
              Permanently deleted models cannot be recovered.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search deleted models..."
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
          />
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-600">
          <span className="text-slate-900 font-black">{filteredModels.length}</span> deleted model{filteredModels.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Deleted Models Grid */}
      {filteredModels.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredModels.map((model, index) => (
              <DeletedModelCard
                key={model.id}
                model={model}
                onRestore={onRestore}
                onPermanentDelete={onPermanentDelete}
                index={index}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-300"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
            <Trash2 className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-lg font-black text-slate-900 mb-2">Recycle Bin is Empty</h3>
          <p className="text-sm text-slate-500 font-medium">
            {searchTerm ? 'No deleted models match your search' : 'No deleted models to show'}
          </p>
        </motion.div>
      )}
    </div>
  );
}
